import { useXAgent, useXChat } from "@ant-design/x";
import { CONFIGS } from "../../configs/configs";
import getBrowserFingerprint from "get-browser-fingerprint";
import useFetch from "../../hooks/useFetch";
import { useEffect, useRef, useState } from "react";

export default function useSendStreamingMcpClient() {
  const [fetchWrapper] = useFetch();
  const [requestLoading, setRequestLoading] = useState(false);

  const abortControllerRef = useRef(null);
  const abortReason = "Cancel Fetch Mcp Client";

  const [xAgent] = useXAgent({
    request: async (info, callbacks) => {
      const {
        conversationKey,
        historyKey,
        thinking,
        debug,
        handleSendSuccess,
        site_id,
      } = info;
      const { onUpdate, onSuccess, onError } = callbacks;

      try {
        let headers = new Headers();
        headers.append("Content-Type", "application/json");

        let csrf_token = null;
        if (getCookie("csrf_access_token") !== undefined)
          csrf_token = getCookie("csrf_access_token");
        if (csrf_token !== null) headers.append("X-CSRF-TOKEN", csrf_token);

        let abortController = new AbortController();
        abortControllerRef.current = abortController;

        const response = await fetch(
          CONFIGS.backend_url + "mcp_client/sendStreamingMcpClient",
          {
            method: "POST",
            headers: headers,
            credentials: "include",
            body: JSON.stringify({
              conversation_id: conversationKey,
              history_id: historyKey,
              thinking: thinking,
              debug: debug,
              site_id: site_id,
            }),
            signal: abortController.signal,
          },
        );

        if (response.status == 401) refreshToken();

        if (!response.body) throw new Error("Response body is null");

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let content = "";

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split("\n\n").filter(Boolean);
          for (const line of lines) {
            content += `\n\n${line}`;
            if (line.includes("Error:")) {
              throw new Error(line.split("Error:")[1].trim());
            }
            onUpdate(content);
          }
        }
        handleSendSuccess();
        onSuccess(content);
      } catch (error) {
        setMessages((prev) => [
          ...prev,
          {
            id: `fake-${messages.length}`,
            message: error.message,
            status: "error",
          },
        ]);
        cancelFetchMcpClient();
        onError(error);
      }
    },
  });

  const {
    messages,
    setMessages,
    onRequest: sendStreamingMcpClient,
  } = useXChat({ agent: xAgent });

  function getCookie(key) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${key}=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
  }

  function refreshToken() {
    const fingerprint = getBrowserFingerprint({
      hardwareOnly: true,
      enableScreen: false,
    });

    return new Promise((resolve, reject) => {
      fetchWrapper({
        endpoint_url: CONFIGS.refresh_token_url,
        params: {
          fingerprint: fingerprint,
        },
        onSuccess: (refresh_status) => {
          if (refresh_status.status) {
            resolve(true);
          } else {
            resolve(false);
          }
        },
        onFetchError: () => {
          resolve(false);
        },
        onResponseError: () => {
          resolve(false);
        },
      });
    });
  }

  function cancelFetchMcpClient() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(abortReason);
      abortControllerRef.current = null;
    }
  }

  useEffect(() => {
    setRequestLoading(xAgent.isRequesting());
  }, [xAgent.isRequesting()]);

  return {
    messages,
    setMessages,
    sendStreamingMcpClient,
    requestLoading,
    cancelFetchMcpClient,
  };
}
